/**
 * Inline-SVG charts for the portal's dashboards.
 *
 * Hand-rolled rather than pulled from a charting library: the figures these render are a
 * handful of scalars from GET /admin/dashboard/metrics, and a chart library would be two
 * orders of magnitude more bundle weight than the shapes actually needed. Everything here
 * is plain SVG with the theme's colors, so it inherits Simply White's look and needs no
 * runtime beyond React.
 *
 * Every component renders its own empty state — dashboards routinely load before the poll
 * resolves, and a chart that silently draws a zero-height bar reads as "no activity"
 * rather than "not loaded yet".
 */

/** MediclynkOS accents, ordered so adjacent series stay distinguishable. */
export const CHART_COLORS = {
  brand: '#93d5e1',
  sky: '#0ea5e9',
  skyDark: '#0284c7',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  neutral: '#cbd5e1',
} as const;

export interface ChartCardProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
}

/** A Pages `.card` with the theme's uppercase 10.5px title and an optional right-aligned hint. */
export function ChartCard({ title, hint, children }: ChartCardProps) {
  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <h2>{title}</h2>
        {hint && <span className="chart-card-hint">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  color: string;
}

/**
 * Vertical bars on a baseline, with value labels above each bar and two faint gridlines.
 * Bars are laid out in a fixed viewBox and scaled by the browser, so the chart is fluid
 * without needing a resize observer.
 */
export function BarChart({ data, height = 180 }: { data: BarDatum[]; height?: number }) {
  if (data.length === 0) return <div className="chart-empty">No data</div>;

  const width = 320;
  const padTop = 24;
  const padBottom = 26;
  const plotHeight = height - padTop - padBottom;
  const slot = width / data.length;
  const barWidth = Math.min(48, slot * 0.5);
  // A flat all-zero series would divide by zero; fall back to a nominal scale so the
  // baseline and labels still render in their right places.
  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={data.map((d) => `${d.label}: ${d.value}`).join(', ')}
    >
      {[0.5, 1].map((fraction) => (
        <line
          key={fraction}
          className="chart-gridline"
          x1={0}
          x2={width}
          y1={padTop + plotHeight * (1 - fraction)}
          y2={padTop + plotHeight * (1 - fraction)}
        />
      ))}
      <line className="chart-gridline" x1={0} x2={width} y1={padTop + plotHeight} y2={padTop + plotHeight} />
      {data.map((datum, index) => {
        const barHeight = Math.max(2, (datum.value / max) * plotHeight);
        const x = slot * index + (slot - barWidth) / 2;
        const y = padTop + plotHeight - barHeight;
        return (
          <g key={datum.label}>
            <rect x={x} y={y} width={barWidth} height={barHeight} rx={2} fill={datum.color} />
            <text className="chart-bar-value" x={x + barWidth / 2} y={y - 7} textAnchor="middle">
              {datum.value}
            </text>
            <text
              className="chart-axis-label"
              x={x + barWidth / 2}
              y={height - 8}
              textAnchor="middle"
            >
              {datum.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

/**
 * A stroke-dasharray donut with the total in the middle. Slices are drawn as arcs on one
 * circle rather than as paths, which keeps the maths to a running offset and gives every
 * slice an identical stroke width.
 */
export function DonutChart({
  slices,
  centerValue,
  centerCaption,
  size = 180,
}: {
  slices: DonutSlice[];
  centerValue: number | string;
  centerCaption: string;
  size?: number;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxHeight: size }}
      role="img"
      aria-label={
        total === 0
          ? `${centerCaption}: none`
          : slices.map((slice) => `${slice.label}: ${slice.value}`).join(', ')
      }
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {/* Track — also the whole chart when the total is zero, so an empty donut reads as
            an empty ring rather than as a missing element. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={CHART_COLORS.neutral}
          strokeOpacity={total === 0 ? 0.4 : 0.25}
          strokeWidth={stroke}
        />
        {total > 0 &&
          slices.map((slice) => {
            const length = (slice.value / total) * circumference;
            const dash = `${length} ${circumference - length}`;
            const element = (
              <circle
                key={slice.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth={stroke}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return element;
          })}
      </g>
      <text
        className="chart-donut-center"
        x={size / 2}
        y={size / 2 + 2}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {centerValue}
      </text>
      <text
        className="chart-donut-caption"
        x={size / 2}
        y={size / 2 + 24}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {centerCaption}
      </text>
    </svg>
  );
}

/**
 * Filled sparkline for a short series. Needs at least two points to mean anything — one
 * sample is a dot, not a trend — so it reports that rather than drawing a flat line.
 */
export function Sparkline({
  values,
  color = CHART_COLORS.sky,
  height = 90,
  emptyLabel = 'Collecting…',
}: {
  values: number[];
  color?: string;
  height?: number;
  emptyLabel?: string;
}) {
  if (values.length < 2) return <div className="chart-empty">{emptyLabel}</div>;

  const width = 320;
  const pad = 6;
  const plotHeight = height - pad * 2;
  const max = Math.max(...values);
  const min = Math.min(...values);
  // A perfectly flat series has no range to normalise against; centre it instead of
  // pinning it to the top of the plot.
  const range = max - min || 1;
  const pointAt = (value: number, index: number) => {
    const x = (index / (values.length - 1)) * width;
    const y = pad + plotHeight * (1 - (value - min) / range);
    return [x, y] as const;
  };

  const points = values.map(pointAt);
  const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `M0,${height} ${points
    .map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ')} L${width},${height} Z`;
  const gradientId = `spark-${color.replace('#', '')}`;

  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Trend from ${values[0]} to ${values[values.length - 1]}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {(() => {
        const [x, y] = points[points.length - 1]!;
        return <circle cx={x} cy={y} r={3} fill={color} vectorEffect="non-scaling-stroke" />;
      })()}
    </svg>
  );
}

export interface LegendItem {
  label: string;
  value?: number | string;
  color: string;
}

export function ChartLegend({ items }: { items: LegendItem[] }) {
  return (
    <div className="chart-legend">
      {items.map((item) => (
        <span className="chart-legend-item" key={item.label}>
          <span className="chart-legend-swatch" style={{ background: item.color }} />
          {item.label}
          {item.value !== undefined && <span className="chart-legend-value">{item.value}</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * The theme's `.progress` bar in a labelled row. Used for ratios where a whole chart would
 * be more furniture than the single number deserves.
 */
export function ProgressMeter({
  label,
  value,
  total,
  color = CHART_COLORS.success,
}: {
  label: string;
  value: number;
  total: number;
  color?: string;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div className="chart-card-head" style={{ marginBottom: '0.45rem' }}>
        <span className="chart-legend-item" style={{ fontSize: '0.78rem' }}>
          {label}
        </span>
        <span className="chart-card-hint">
          {total > 0 ? `${value} of ${total} · ${pct}%` : 'No cases yet today'}
        </span>
      </div>
      <div
        className="progress progress-small"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        style={{ background: 'rgba(226, 232, 240, 0.9)', borderRadius: 3, height: 6 }}
      >
        <div
          className="progress-bar"
          style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 3 }}
        />
      </div>
    </div>
  );
}
